import React, { useCallback, useState, type JSX } from "react";
import { useAppSelector, useAppDispatch } from "./hooks/reduxHooks";
import { useFeatureFlag } from "./hooks/useFeatureFlag";
import { useStreamingMessage } from "./hooks/useStreamingMessage";
import {
  selectResultsForTurn,
  selectResearchDocsForTurn,
  selectExpertsForTurn,
} from "./ResultsSlice_Store";
import { openExpertProfile } from "./ExpertsSlice_Store";
import { openDealDetail, fetchDealDetail } from "./DealsTableSlice_Store";
import { openResearchDoc, fetchResearchDocDetail } from "./ResearchDocsSlice_Store";
import { openDocumentPreview } from "./DocumentPreviewSlice_Store";
import { setLayoutMode } from "./LayoutSlice_Store";
import { canPreviewDocument } from "./services/documentPreviewService";
import InlineExpertsTable from "./InlineExpertsTable_Component";
import InlineDealsTable from "./InlineDealsTable_Component";
import InlineResearchDocsTable from "./InlineResearchDocsTable_Component";
import NonPreviewableDocumentModal from "./components/NonPreviewableDocumentModal";
import type { Deal, Expert, ResearchDoc } from "./types/redux";
import { decideTabVisibility, type ResultsTab, type TabCounts } from "./utils/inlineResultsTabs";

interface Props {
  turnId: string | undefined;
}

/** A turn is "streaming" while text is still arriving or the typing animation is running. */
function useIsStreaming(turnId: string | undefined): boolean {
  const streamingData = useStreamingMessage(turnId || "");
  return (
    !!streamingData.displayedText &&
    !(streamingData.isComplete && !streamingData.isAnimating)
  );
}

function useDealClick(): (deal: Deal) => void {
  const dispatch = useAppDispatch();
  return useCallback(
    (deal: Deal) => {
      const dealId = deal.dealId || deal.id;
      if (!dealId) return;
      dispatch(openDealDetail(dealId));
      void dispatch(fetchDealDetail(dealId));
      dispatch(setLayoutMode("workspace"));
    },
    [dispatch]
  );
}

function useExpertClick(): (expert: Expert) => void {
  const dispatch = useAppDispatch();
  return useCallback(
    (expert: Expert) => {
      dispatch(openExpertProfile(expert));
      dispatch(setLayoutMode("workspace"));
    },
    [dispatch]
  );
}

function useDocClick(
  onNonPreviewable: (doc: ResearchDoc) => void
): (doc: ResearchDoc) => void {
  const dispatch = useAppDispatch();
  return useCallback(
    (doc: ResearchDoc) => {
      if (!doc.docId) return;
      // Track selection in ResearchDocsSlice. Only fire the detail-fetch thunk
      // when a dedicated research-docs base URL is actually configured — the
      // endpoint is still stubbed (Q4), so an unconditional dispatch in
      // unconfigured environments just populates detailError with noise.
      dispatch(openResearchDoc(doc.docId));
      if (import.meta.env.VITE_RESEARCH_DOCS_API_BASE_URL) {
        void dispatch(fetchResearchDocDetail(doc.docId));
      }

      // Non-previewable types (e.g. .xlsx without SharePoint IDs) fall through
      // to the explanatory modal — same UX as fact-badge clicks.
      if (!canPreviewDocument(doc)) {
        onNonPreviewable(doc);
        return;
      }

      // Mirror DealDocsPanel + InlineFactBadge: normalize docId onto the
      // payload and switch to workspace so the RHS pane is visible. Preserve
      // originalFilename intact — the preview pane reads it for file-type
      // detection. The tab label prefers researchTitle (see DealsLayout).
      dispatch(openDocumentPreview({ ...doc, docId: doc.docId }));
      dispatch(setLayoutMode("workspace"));
    },
    [dispatch, onNonPreviewable]
  );
}

function NonPreviewableDoc({
  doc,
  onClose,
}: {
  doc: ResearchDoc | null;
  onClose: () => void;
}): JSX.Element {
  return (
    <NonPreviewableDocumentModal
      isOpen={!!doc}
      onClose={onClose}
      documentName={
        doc?.researchTitle || doc?.originalFilename || doc?.documentName || "Unknown document"
      }
      filePath={doc?.filePath ?? undefined}
      sharepointSiteId={doc?.sharepointSiteId ?? undefined}
      sharepointDriveId={doc?.sharepointDriveId ?? undefined}
      sharepointItemId={doc?.sharepointItemId ?? undefined}
    />
  );
}

const TAB_LABELS: Record<ResultsTab, string> = {
  experts: "Experts",
  deals: "Deals",
  research: "Research",
};

function ResultsTabBar({
  tabs,
  current,
  counts,
  onSelect,
}: {
  tabs: ResultsTab[];
  current: ResultsTab;
  counts: TabCounts;
  onSelect: (tab: ResultsTab) => void;
}): JSX.Element {
  return (
    <div
      role="tablist"
      aria-label="Results"
      className="flex items-center gap-1 mt-3 border-b border-cvc-tableBorder"
    >
      {tabs.map((tab) => (
        <TabButton
          key={tab}
          label={`${TAB_LABELS[tab]} (${counts[tab]})`}
          active={current === tab}
          onClick={() => onSelect(tab)}
        />
      ))}
    </div>
  );
}

function useTurnEvidence(turnId: string | undefined) {
  const turnResults = useAppSelector((state) => selectResultsForTurn(state, turnId));
  const researchDocs: ResearchDoc[] = useAppSelector((state) =>
    selectResearchDocsForTurn(state, turnId)
  );
  const experts: Expert[] = useAppSelector((state) => selectExpertsForTurn(state, turnId));
  return { turnResults, researchDocs, experts };
}

export default function InlineResultsTabs({ turnId }: Props): JSX.Element | null {
  const flagOn = useFeatureFlag("inlineDeals");
  const { turnResults, researchDocs, experts } = useTurnEvidence(turnId);
  const isStreaming = useIsStreaming(turnId);

  const deals: Deal[] = turnResults?.deals ?? [];
  const documents = turnResults?.documents ?? [];

  const counts: TabCounts = {
    experts: experts.length,
    deals: deals.length,
    research: researchDocs.length,
  };
  const visibility = decideTabVisibility(counts);
  const [activeTab, setActiveTab] = useState<ResultsTab | null>(visibility.defaultTab);
  const [nonPreviewableDoc, setNonPreviewableDoc] = useState<ResearchDoc | null>(null);

  const handleDealClick = useDealClick();
  const handleExpertClick = useExpertClick();
  const handleDocClick = useDocClick(setNonPreviewableDoc);

  if (!flagOn || isStreaming) return null;
  if (!visibility.defaultTab) return null;

  const current =
    activeTab && visibility.tabs.includes(activeTab) ? activeTab : visibility.defaultTab;

  const expertsTable = <InlineExpertsTable experts={experts} onExpertClick={handleExpertClick} />;

  const dealsTable = (
    <InlineDealsTable deals={deals} documents={documents} onDealClick={handleDealClick} />
  );

  const researchTable = (
    <InlineResearchDocsTable researchDocs={researchDocs} onDocClick={handleDocClick} />
  );

  const nonPreviewableModal = (
    <NonPreviewableDoc doc={nonPreviewableDoc} onClose={() => setNonPreviewableDoc(null)} />
  );

  const tables: Record<ResultsTab, JSX.Element> = {
    experts: expertsTable,
    deals: dealsTable,
    research: researchTable,
  };

  if (!visibility.showTabChrome) {
    return (
      <>
        {tables[visibility.defaultTab]}
        {nonPreviewableModal}
      </>
    );
  }

  return (
    <div>
      <ResultsTabBar
        tabs={visibility.tabs}
        current={current}
        counts={counts}
        onSelect={setActiveTab}
      />
      {tables[current]}
      {nonPreviewableModal}
    </div>
  );
}

interface TabButtonProps {
  label: string;
  active: boolean;
  onClick: () => void;
}

function TabButton({ label, active, onClick }: TabButtonProps): JSX.Element {
  return (
    <button
      type="button"
      role="tab"
      aria-selected={active}
      onClick={onClick}
      className={`px-3 py-1.5 text-sm font-cvcBody transition-colors border-b-2 -mb-px ${
        active
          ? "border-cvc-blue text-cvc-blue"
          : "border-transparent text-cvc-headerText hover:text-cvc-blue"
      }`}
    >
      {label}
    </button>
  );
}
